import React, { useState } from 'react'
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { Link } from 'react-router-dom'
import { useI18n } from '@/contexts/I18nContext'
import { usePhieuTamTonKhoAlert, type PhieuTamTonKhoGroup } from '@/hooks/usePhieuTamTonKhoAlert'
import routerConstants from '@/constants/routerConstants'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import { EntityCodeLink } from '@/components/admin/_shared/entityCode'
import type { EntityCodeType } from '@/components/admin/_shared/entityCode/entityCodeTypes'

const WARN = '#b45309'
const WARN_BG = '#fff7ed'
const WARN_BORDER = '#fdba74'

type SectionProps = {
  title: string
  listUrl: string
  group: PhieuTamTonKhoGroup
  entityType: EntityCodeType
  onNavigate: () => void
  viewAllLabel: string
  andMoreLabel: (count: number) => string
}

const DraftSection: React.FC<SectionProps> = ({
  title,
  listUrl,
  group,
  entityType,
  onNavigate,
  viewAllLabel,
  andMoreLabel,
}) => {
  if (group.total <= 0) return null
  return (
    <Box sx={{ px: 1.5, py: 1.1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
          {title} ({group.total})
        </Typography>
        <Typography
          component={Link}
          to={listUrl}
          onClick={onNavigate}
          sx={{ fontSize: 12, fontWeight: 700, color: banhangKiotLike.linkBlue, textDecoration: 'none' }}
        >
          {viewAllLabel}
        </Typography>
      </Box>
      <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.35 }} onClick={onNavigate}>
        {group.items.map((item) => (
          <EntityCodeLink key={item.id} type={entityType} id={item.id} code={item.code} />
        ))}
        {group.total > group.items.length ? (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {andMoreLabel(group.total - group.items.length)}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

/** Icon cảnh báo gọn sau nút Bán hàng — số phiếu tạm KK/CH/NK/XK. */
const PhieuTamTonKhoHeaderAlert: React.FC = () => {
  const { t } = useI18n()
  const {
    kiemKe,
    chuyenHang,
    nhapKho,
    xuatKho,
    totalCount,
    canKiemKe,
    canChuyenHang,
    canNhapKho,
    canXuatKho,
  } = usePhieuTamTonKhoAlert()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)

  if ((!canKiemKe && !canChuyenHang && !canNhapKho && !canXuatKho) || totalCount <= 0) return null

  const close = () => setAnchor(null)
  const viewAll = t('components.phieuTamTonKho.viewAll')
  const andMore = (count: number) => t('components.phieuTamTonKho.andMore', { count })
  const aria = t('components.phieuTamTonKho.headerAria', { count: totalCount })
  const tip = t('components.phieuTamTonKho.headerLabelWithCount', { count: totalCount })

  const sections: Array<{
    show: boolean
    title: string
    listUrl: string
    group: PhieuTamTonKhoGroup
    entityType: EntityCodeType
  }> = [
    {
      show: canKiemKe && kiemKe.total > 0,
      title: t('components.menu.kiemKeList'),
      listUrl: `${routerConstants.kiemKe.list}?trangThai=phieuTam`,
      group: kiemKe,
      entityType: 'kiemKe',
    },
    {
      show: canChuyenHang && chuyenHang.total > 0,
      title: t('components.menu.chuyenHangList'),
      listUrl: `${routerConstants.chuyenHang.list}?trangThai=phieuTam`,
      group: chuyenHang,
      entityType: 'chuyenHang',
    },
    {
      show: canNhapKho && nhapKho.total > 0,
      title: t('components.menu.phieuNhapKhoList'),
      listUrl: `${routerConstants.phieuNhapKho.list}?trangThai=phieuTam`,
      group: nhapKho,
      entityType: 'phieuNhapKho',
    },
    {
      show: canXuatKho && xuatKho.total > 0,
      title: t('components.menu.phieuXuatKhoList'),
      listUrl: `${routerConstants.phieuXuatKho.list}?trangThai=phieuTam`,
      group: xuatKho,
      entityType: 'phieuXuatKho',
    },
  ]
  const visible = sections.filter((s) => s.show)

  return (
    <>
      <Tooltip title={tip} arrow>
        <Badge
          badgeContent={totalCount}
          color="error"
          max={99}
          overlap="circular"
          sx={{
            alignSelf: 'stretch',
            height: '100%',
            display: 'inline-flex',
            alignItems: 'stretch',
            '& .MuiBadge-badge': {
              fontWeight: 800,
              fontSize: 10,
              minWidth: 16,
              height: 16,
              px: 0.4,
              right: 2,
              top: 2,
            },
          }}
        >
          <IconButton
            color="inherit"
            size="small"
            disableRipple
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={anchor ? 'true' : undefined}
            aria-label={aria}
            sx={{
              alignSelf: 'stretch',
              height: '100%',
              minHeight: 0,
              minWidth: 36,
              px: 0.75,
              color: WARN,
              bgcolor: WARN_BG,
              border: `1px solid ${WARN_BORDER}`,
              borderRadius: `${banhangKiotLike.navItemActiveRadiusPx}px`,
              '&:hover': {
                bgcolor: '#ffedd5',
                color: WARN,
              },
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Badge>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 360,
              maxWidth: '92vw',
              maxHeight: '70vh',
              borderRadius: `${banhangKiotLike.filterSidebarCardRadiusPx}px`,
              border: `1px solid ${WARN_BORDER}`,
              boxShadow: '0 10px 28px rgba(120, 53, 15, 0.18)',
              overflow: 'auto',
            },
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25, bgcolor: WARN_BG, position: 'sticky', top: 0, zIndex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: WARN, lineHeight: 1.35 }}>
            {t('components.phieuTamTonKho.panelTitle', { count: totalCount })}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: '#9a3412', lineHeight: 1.4 }}>
            {t('components.phieuTamTonKho.panelHint')}
          </Typography>
        </Box>

        {visible.map((s, idx) => (
          <React.Fragment key={s.entityType}>
            {idx > 0 ? <Divider /> : null}
            <DraftSection
              title={s.title}
              listUrl={s.listUrl}
              group={s.group}
              entityType={s.entityType}
              onNavigate={close}
              viewAllLabel={viewAll}
              andMoreLabel={andMore}
            />
          </React.Fragment>
        ))}
      </Popover>
    </>
  )
}

export default PhieuTamTonKhoHeaderAlert
